'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';

import { useLocaleFormat } from '@/shared/hooks/useLocaleFormat';
import { useNotificationMessageSignal } from '@/shared/providers/notifications-provider/store';
import { useIsAuth } from '@/shared/providers/user-provider/use-user';

import type { GamingLimitHardRejectionPayload } from '../../model/gaming-limits/gaming-limits.types';
import { parseGamingLimitHardRejectionMessage } from '../../model/gaming-limits/gaming-limits-event';
import { getGamingLimitsProfilePath } from '../../model/gaming-limits/gaming-limits.navigation';
import { isGamingLimitsEnabled } from '../../model/gaming-limits/gaming-limits.rollout';
import { GamingLimitsHardModal } from './gaming-limits-hard-modal';

interface GamingLimitsHardModalHostProps {
  children: ReactNode;
}

const EnabledGamingLimitsHardModalHost = ({
  children,
}: GamingLimitsHardModalHostProps) => {
  const router = useRouter();
  const formatUrl = useLocaleFormat();
  const isAuth = useIsAuth();
  const messageSignal = useNotificationMessageSignal();
  const [rejection, setRejection] =
    useState<GamingLimitHardRejectionPayload | null>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const lastHandledSequenceRef = useRef(0);

  useEffect(() => {
    if (!isAuth) {
      lastHandledSequenceRef.current = 0;
      setRejection(null);
      return;
    }

    if (
      !messageSignal ||
      messageSignal.sequence <= lastHandledSequenceRef.current
    ) {
      return;
    }

    lastHandledSequenceRef.current = messageSignal.sequence;
    if (rejection) return;

    const nextRejection = parseGamingLimitHardRejectionMessage(
      messageSignal.raw
    );
    if (nextRejection) setRejection(nextRejection);
  }, [isAuth, messageSignal, rejection]);

  useLayoutEffect(() => {
    if (!rejection) {
      setPortalTarget(null);
      return;
    }

    const syncPortalTarget = () => {
      setPortalTarget(
        document.fullscreenElement instanceof HTMLElement
          ? document.fullscreenElement
          : document.body
      );
    };

    syncPortalTarget();
    document.addEventListener('fullscreenchange', syncPortalTarget);

    return () => {
      document.removeEventListener('fullscreenchange', syncPortalTarget);
    };
  }, [rejection]);

  useLayoutEffect(() => {
    if (!rejection || !portalTarget) return;

    const root = document.createElement('div');
    root.dataset.gamingLimitsHardModalRoot = 'true';
    portalTarget.appendChild(root);

    const obscuredElements = Array.from(portalTarget.children).filter(
      (element): element is HTMLElement =>
        element instanceof HTMLElement && element !== root
    );
    const previousElementState = obscuredElements.map((element) => ({
      element,
      inert: element.hasAttribute('inert'),
      ariaHidden: element.getAttribute('aria-hidden'),
    }));
    const previousBodyOverflow = document.body.style.overflow;

    obscuredElements.forEach((element) => {
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    });
    document.body.style.overflow = 'hidden';
    setPortalRoot(root);

    return () => {
      setPortalRoot(null);
      previousElementState.forEach(({ element, inert, ariaHidden }) => {
        if (!inert) element.removeAttribute('inert');
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      document.body.style.overflow = previousBodyOverflow;
      root.remove();
    };
  }, [portalTarget, rejection]);

  const closeModal = useCallback(() => setRejection(null), []);

  const finishGame = useCallback(() => {
    closeModal();
    router.push(formatUrl('/'));
  }, [closeModal, formatUrl, router]);

  const changeLimit = useCallback(() => {
    closeModal();
    router.push(formatUrl(getGamingLimitsProfilePath()));
  }, [closeModal, formatUrl, router]);

  return (
    <>
      {children}
      {rejection &&
        portalRoot &&
        createPortal(
          <GamingLimitsHardModal
            rejection={rejection}
            onFinishGame={finishGame}
            onChangeLimit={changeLimit}
          />,
          portalRoot
        )}
    </>
  );
};

export const GamingLimitsHardModalHost = ({
  children,
}: GamingLimitsHardModalHostProps) => {
  if (!isGamingLimitsEnabled()) return children;

  return (
    <EnabledGamingLimitsHardModalHost>
      {children}
    </EnabledGamingLimitsHardModalHost>
  );
};
